import React, {useEffect, useState} from 'react'
import Menu from '@mui/material/Menu';
import {Avatar, Badge, IconButton, ListItemIcon, MenuItem, useMediaQuery, useTheme} from '@mui/material';
import Divider from '@mui/material/Divider';
import Logout from '@mui/icons-material/Logout';
import Settings from '@mui/icons-material/Settings';
import api from '../../api/axios';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {signOut} from '../../store/slices/authSlice';
import {RootState} from '../../store';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import {Autorenew, CreditCard, PermIdentity, Person, Sell, Support} from '@mui/icons-material';
import Wallet from './wallet/Wallet';
import {useDepositWithdrawModal} from './wallet/DepositWithdrawModalContext';

const NavbarProfile = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmUp = useMediaQuery('(min-width:640px)');

    const {openModal} = useDepositWithdrawModal();

    const avatarUrl = useSelector((state: RootState) => state.auth.selectedSteamAccount?.avatarUrl);
    const role = useSelector((state: RootState) => state.auth.role);
    const pendingOffers = useSelector((state: RootState) => state.auth.statistics?.pendingOffers || 0);
    const actionableTrades = useSelector((state: RootState) => state.auth.statistics?.actionableTrades || 0);
    const totalActionable = pendingOffers + actionableTrades;
    const hasActionableItems = totalActionable > 0;

    const [hasAdminAccess, setHasAdminAccess] = useState(false);

    useEffect(() => {
        const allowedAdminRoles = ['SYSTEM_ADMIN', 'ADMIN', 'SUPPORT'];
        setHasAdminAccess(role !== null && allowedAdminRoles.includes(role));
    }, [role]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = async () => {
        try {
            const promise = api.post("/sign-out");
            await toast.promise(promise, {
                pending: t("toastify.pending"),
                success: t("signOut.success"),
                error: t("signOut.error"),
            });
            dispatch(signOut());
            navigate("/");
            await api.get("/check-authentication");
        } catch (err) {
        }
    };

    return (
        <div className="flex items-center">
            <IconButton
                onClick={handleClick}
                size="small"
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{color: 'text.secondary', '&:hover': {backgroundColor: 'action.hover'}}}
            >
                <Badge
                    badgeContent={totalActionable > 99 ? '99+' : totalActionable}
                    color="error"
                    invisible={!hasActionableItems || isSmUp}
                    sx={{
                        '& .MuiBadge-badge': {
                            right: -2,
                            top: -2,
                        },
                    }}
                >
                    <Avatar
                        src={avatarUrl ?? undefined}
                        sx={{
                            width: 32,
                            height: 32,
                            ...(hasActionableItems && !isSmUp && {
                                boxShadow: '0 0 0 4px #ef4444',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                            })
                        }}
                    >
                        {!avatarUrl && <Person fontSize="medium"/>}
                    </Avatar>
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                disableScrollLock={true}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            border: '1px solid #404040',
                            borderRadius: '8px',
                            mt: 1.5,
                            '& .MuiAvatar-root': {width: 32, height: 32, ml: -0.5, mr: 1},
                            '&::before': {
                                content: '""', display: 'block', position: 'absolute', top: 0, right: 18,
                                width: 12, height: 12, bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
                                borderLeft: '1px solid #404040', borderTop: '1px solid #404040',
                            },
                        },
                    },
                }}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <div className="block lg:hidden">
                    <MenuItem className="!py-2"><Wallet/></MenuItem>
                    <Divider/>
                </div>

                <MenuItem onClick={() => {
                    handleClose();
                    navigate('/profile');
                }} className="!py-2 !font-semibold">
                    <ListItemIcon><PermIdentity className="text-blue-500" fontSize="small"/></ListItemIcon>
                    Profil
                </MenuItem>

                {hasAdminAccess && (
                    <>
                        <Divider/>
                        <MenuItem onClick={() => {
                            handleClose();
                            navigate('/system');
                        }} className="!py-2 !font-semibold">
                            <ListItemIcon><Settings className="text-amber-500" fontSize="small"/></ListItemIcon>
                            System
                        </MenuItem>
                    </>
                )}

                <Divider/>

                <MenuItem onClick={() => {
                    handleClose();
                    openModal('withdraw');
                }} className="!py-2 !font-semibold">
                    <ListItemIcon><CreditCard className="text-emerald-500" fontSize="small"/></ListItemIcon>
                    Withdraw
                </MenuItem>

                <MenuItem onClick={() => {
                    handleClose();
                    navigate('/sell');
                }} className="!py-2 !font-semibold">
                    <ListItemIcon><Sell className="text-indigo-500" fontSize="small"/></ListItemIcon>
                    Verkaufen
                </MenuItem>

                <MenuItem onClick={() => {
                    handleClose();
                    navigate('/profile/trades/sold');
                }} className="!py-2 !font-semibold flex justify-between items-center">
                    <div className="flex items-center">
                        <ListItemIcon>
                            <Autorenew className="text-orange-500" fontSize="small"/>
                        </ListItemIcon>
                        <Badge badgeContent={totalActionable > 99 ? '99+' : totalActionable}
                               color="error"
                               invisible={!hasActionableItems}
                               sx={{
                                   '& .MuiBadge-badge': {
                                       right: -10,
                                       top: 4,
                                   },
                               }}>
                            Trades
                        </Badge>
                    </div>
                </MenuItem>

                <Divider/>

                <MenuItem onClick={() => {
                    handleClose();
                    if ((window as any).openCrispChat) {
                        (window as any).openCrispChat();
                    }
                }} className="!py-2 !font-semibold">
                    <ListItemIcon><Support className="text-sky-500" fontSize="small"/></ListItemIcon>
                    Support
                </MenuItem>

                <Divider/>

                <MenuItem onClick={async () => {
                    handleClose();
                    await handleSignOut();
                }} className="!py-2 !font-semibold">
                    <ListItemIcon><Logout className="text-rose-500" fontSize="small"/></ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </div>
    )
}

export default NavbarProfile